import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface RoleBasedRouteProps {
  children: ReactNode;
  allowedRoles: string[];
}

const normalizeRole = (role?: string) => role?.trim().toUpperCase().replace(/[\s_-]+/g, ' ') || '';

const roleAliases: Record<string, string[]> = {
  'SUPER ADMIN': ['SUPER ADMIN', 'ADMIN', 'SYSTEM ADMINISTRATOR', 'ADMINISTRATOR', 'STATION MANAGER'],
  'ADMIN': ['ADMIN', 'SYSTEM ADMINISTRATOR', 'ADMINISTRATOR', 'STATION MANAGER'],
  'STATION MANAGER': ['ADMIN', 'SYSTEM ADMINISTRATOR', 'ADMINISTRATOR', 'STATION MANAGER'],
  'SYSTEM ADMINISTRATOR': ['ADMIN', 'SYSTEM ADMINISTRATOR', 'ADMINISTRATOR', 'STATION MANAGER'],
  'ADMINISTRATOR': ['ADMIN', 'SYSTEM ADMINISTRATOR', 'ADMINISTRATOR', 'STATION MANAGER'],
  'CONTROLLER': ['CONTROLLER'],
  'OFFICE': ['OFFICE'],
  'CLEARER': ['CLEARER'],
};

export const isRoleAllowed = (userRole: string | undefined, allowedRoles: string[]) => {
  const normalizedUserRole = normalizeRole(userRole);
  if (!normalizedUserRole) return false;
  if (normalizedUserRole === 'SUPER ADMIN') return true;

  return allowedRoles.some((allowedRole) => {
    const normalizedAllowedRole = normalizeRole(allowedRole);
    if (normalizedAllowedRole === normalizedUserRole) return true;
    return (roleAliases[normalizedAllowedRole] || []).includes(normalizedUserRole);
  });
};

const RoleBasedRoute = ({ children, allowedRoles }: RoleBasedRouteProps) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user) {
    const userRole = typeof user.role === 'string' ? user.role : user.role?.name;
    if (!isRoleAllowed(userRole, allowedRoles)) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return <>{children}</>;
};

export default RoleBasedRoute;
